import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Box,
  MenuItem,
  InputAdornment,
  Typography,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material'
import keys from '@/utils/keys'
import { StateSchema } from '@/hooks/form/models'

import { BonusAmountType, BonusType, CurrencyCodeEnum } from '@/enums'
import NumberFormatCustom from '@/views/components/input-number-format/index.component'
import { BonusTriggerType } from '@/enums/be-bonus-trigger-type.enum'

import CurrenciesTableComponent from '@/views/components/currencies-table/currencies-table.components'
import { authSelector } from '@/state/auth/slices/index.slices'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
  triggerState: StateSchema
  bonusType: string
}

const BonusAmountComponent = ({
  handleOnChange,
  state,
  triggerState,
  bonusType,
}: Props) => {
  const { t } = useTranslation()

  const currencyPercentageCells = ['baseCurrency', 'percentage', 'cap']
  const currencyFixedCells = ['baseCurrency', 'fixed']

  const { brand } = useSelector(authSelector)

  const [bonusAmountTypes, setBonusAmountType] = useState(
    [
      ...keys(BonusAmountType)
        .filter(
          (item) =>
            typeof BonusAmountType[item] !== 'string' &&
            BonusAmountType[item] !== BonusAmountType.FREE_SPINS_AMOUNT,
        )
        .map((x) => {
          return {
            index: BonusAmountType[x],
            label: x,
          }
        }),
    ].sort((a, b) => a.label.localeCompare(b.label)),
  )

  useEffect(() => {
    if (
      triggerState.triggerBy.value === BonusTriggerType.GAMIFICATION ||
      triggerState.triggerBy.value === BonusTriggerType.BY_REGISTRATION ||
      triggerState.triggerBy.value === BonusTriggerType.BY_CODE ||
      (triggerState.triggerBy.value === BonusTriggerType.BY_BUNDLE &&
        (bonusType === BonusType.CASINO_STANDARD ||
          bonusType === BonusType.BETSOFT_CASINO))
      // ||         bonusType === BonusType.FREE_PLAY))
    ) {
      // eslint-disable-next-line
      setBonusAmountType(
        bonusAmountTypes.filter((amount) => amount.label === 'FIXED'),
      )
    }
  }, [triggerState]) // eslint-disable-line

  return (
    <>
      <Box maxWidth={400}>
        <Typography fontWeight={700} sx={{ mb: '20px' }}>
          {t('bonusAmountType')}
        </Typography>
        <TextField
          fullWidth
          name="amountType"
          label={t('selectBonusAmountType')}
          value={state.amountType.value || ''}
          onChange={(e: any) => handleOnChange('amountType', e.target.value)}
          select
        >
          {(bonusAmountTypes || []).map((item) => (
            <MenuItem key={item.index} value={item.label}>
              {t(`bonusAmountTypes.${BonusAmountType[item.index]}`)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box maxWidth={800} mb={2} mt={3}>
        {state.amountType.value !== '' && (
          <Typography fontWeight={700} gutterBottom sx={{ mb: '15px' }}>
            {t('labels.bonusAmount')}
          </Typography>
        )}
        {state.amountType.value === 'PERCENTAGE' &&
          triggerState.triggerBy.value !== BonusTriggerType.GAMIFICATION &&
          triggerState.triggerBy.value !== BonusTriggerType.BY_REGISTRATION && (
            <CurrenciesTableComponent
              currencyHeadCells={currencyPercentageCells}
            >
              {brand.currencies.map((currency) => (
                <TableRow key={`percentage_${currency}`}>
                  <TableCell>
                    {currency === CurrencyCodeEnum.USD
                      ? `${currency} ${t('labels.currencyDefault')}`
                      : currency}
                  </TableCell>
                  <TableCell sx={{ width: 300 || 'auto' }}>
                    <NumberFormatCustom
                      name={`percentage_${currency}`}
                      handleOnChange={handleOnChange}
                      state={state}
                      currency={currency}
                      prefix=""
                      style={{ padding: '16px 0' }}
                      InputPropsAdornment={{
                        endAdornment: (
                          // eslint-disable-next-line
                          <InputAdornment position="end">{'%'}</InputAdornment>
                        ),
                        inputProps: {
                          maxLength: 6,
                        },
                      }}
                      fullwidth
                    />
                  </TableCell>

                  <TableCell sx={{ width: 300 || 'auto' }}>
                    <NumberFormatCustom
                      name={`cap_${currency}`}
                      handleOnChange={handleOnChange}
                      state={state}
                      currency={currency}
                      style={{ padding: '16px 0' }}
                      prefix=""
                      fullwidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </CurrenciesTableComponent>
          )}
        {state.amountType.value === 'FIXED' && (
          <CurrenciesTableComponent currencyHeadCells={currencyFixedCells}>
            {brand.currencies.map((currency) => (
              <TableRow key={`fixed_${currency}`}>
                <TableCell>
                  {currency === CurrencyCodeEnum.USD
                    ? `${currency} ${t('labels.currencyDefault')}`
                    : currency}
                </TableCell>
                <TableCell sx={{ width: 300 || 'auto' }}>
                  <NumberFormatCustom
                    name={`fixed_${currency}`}
                    handleOnChange={handleOnChange}
                    state={state}
                    currency={currency}
                    prefix=""
                  />
                </TableCell>
              </TableRow>
            ))}
          </CurrenciesTableComponent>
        )}
      </Box>
    </>
  )
}

export default BonusAmountComponent
